import React, { useState, useEffect, useCallback, useRef } from "react";
import { auth } from "@/shared/lib/firebase";
import { RecaptchaVerifier, signInWithPhoneNumber } from "firebase/auth";
import { PhoneAuthProvider, signInWithCredential } from "firebase/auth";
import { ReactComponent as USFlag } from "@/assets/flags/us.svg";
import { ReactComponent as CanadaFlag } from "@/assets/flags/canada.svg";
import { ReactComponent as IndiaFlag } from "@/assets/flags/india.svg";

// Country code options
const COUNTRY_OPTIONS = [
  { code: "+1", country: "US", Flag: USFlag },
  { code: "+1", country: "CA", Flag: CanadaFlag },
  { code: "+91", country: "IN", Flag: IndiaFlag },
] as const;

type CountryOption = typeof COUNTRY_OPTIONS[number];

declare global {
    interface Window {
      recaptchaVerifier: any;
    }
  }

  interface RegPlatformProps {
    email:    string;
    username: string;
    discordId: string;
    discordAvatar: string;
    nextStep: () => void;
}

const Reg_PhoneVerification: React.FC<RegPlatformProps> = ({ email, username, discordId, discordAvatar, nextStep }) => {
  const [phoneNumber, setPhoneNumber] = useState("");
  const [code, setCode] = useState("");
  const [verificationId, setVerificationId] = useState<string | null>(null);
  const [firebaseToken, setFirebaseToken] = React.useState("");
  const [verifiedPhone, setVerifiedPhone] = React.useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState<CountryOption>(COUNTRY_OPTIONS[0]);
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

    const onPhoneVerified = (token: string, phone: string) => {
        setFirebaseToken(token);
        setVerifiedPhone(phone);
    };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowCountryDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSubmit = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/register`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({
          email: email,
          username: username,
          phoneNumber: `${selectedCountry.code}${verifiedPhone.replace(/-/g, '')}`,
          firebaseToken: firebaseToken,
          discordId: discordId,
          discordAvatar: discordAvatar,
        }),
      });

      if (!response.ok) {
        const text = await response.text();
        setError(text || "Registration failed");
        setLoading(false);
        return;
      }

      setDone(true);
      setLoading(false);
    } catch (err: unknown) {
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError("Network error");
      }
      setLoading(false);
    }
  }, [email, username, verifiedPhone, firebaseToken, selectedCountry.code, discordId, discordAvatar]); 

  React.useEffect(() => {
    if (done) {
      nextStep();
    }
  }, [done, nextStep]);

  React.useEffect(() => {
    if (firebaseToken && verifiedPhone) {
        handleSubmit();
    }
  }, [firebaseToken, verifiedPhone, handleSubmit]);

  useEffect(() => {
    if (!window.recaptchaVerifier) {
      window.recaptchaVerifier = new RecaptchaVerifier(auth, 'recaptcha-container', {
        size: 'invisible',
        callback: (response: string) => {
        }
      });
      window.recaptchaVerifier.render();
    }
  }, []);
  
  const sendCode = async () => {
    setError(null);
    try {
      const appVerifier = window.recaptchaVerifier;
      const sanitizedPhone = phoneNumber.replace(/-/g, '');
      const confirmationResult = await signInWithPhoneNumber(auth, `${selectedCountry.code}${sanitizedPhone}`, appVerifier);
      setVerificationId(confirmationResult.verificationId);
    } catch (err: any) { 

      console.error("Failed to send SMS:", err);
      setError("Failed to send SMS: " + err.message);
    }
  };

  const verifyCode = async () => {
    if (!verificationId) {
      setError("No verification ID");
      return;
    }
    try {
        const credential = PhoneAuthProvider.credential(verificationId, code);
        const userCredential = await signInWithCredential(auth, credential);
        const token = await userCredential.user.getIdToken();

        onPhoneVerified(token, phoneNumber);
    } catch (err: any) {
      console.error("Invalid verification code:", err);
      setError("Invalid verification code: " + err.message);
    }
  };

  return (
    <div>
      <h3 style={{ margin: "0px" }}>Enter {!verificationId ? "Phone Number" : "Verification Code"}</h3>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', marginBottom: '1rem', alignItems: 'center'}}>
      <div id="recaptcha-container"></div>
      {!verificationId ? (
        <>
         <div className="inputGroup" ref={dropdownRef}>
          <div
            className="countrySelector"
            onClick={() => setShowCountryDropdown(!showCountryDropdown)}
          >
            <selectedCountry.Flag style={{ width: '24px', height: '18px', borderRadius: '2px' }} />
            <span className="countryCode">{selectedCountry.code}</span>
            <span className="dropdownArrow">&#9660;</span>
          </div>
          {showCountryDropdown && (
            <div className="countryDropdown">
              {COUNTRY_OPTIONS.map((option, index) => (
                <div
                  key={`${option.country}-${index}`}
                  className="countryOption"
                  onClick={() => {
                    setSelectedCountry(option);
                    setShowCountryDropdown(false);
                  }}
                >
                  <option.Flag style={{ width: '24px', height: '18px', borderRadius: '2px' }} />
                  <span>{option.country}</span>
                  <span className="optionCode">{option.code}</span>
                </div>
              ))}
            </div>
          )}
          <input
            data-cy="input-phone"
            type="tel"
            placeholder="Phone Number"
            className="inputField phoneInput"
            value={phoneNumber}
            onChange={(e) => setPhoneNumber(e.target.value)}
          />
          <button
            data-cy="submit-btn"
            onClick={sendCode}
            disabled={loading || phoneNumber.length < 10}
            className={`inputSubmit phoneSubmit${phoneNumber.length >= 10 ? " valid" : ""}`}
          >
            <svg width={20} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
              <path fillRule="evenodd" d="M12.97 3.97a.75.75 0 0 1 1.06 0l7.5 7.5a.75.75 0 0 1 0 1.06l-7.5 7.5a.75.75 0 1 1-1.06-1.06l6.22-6.22H3a.75.75 0 0 1 0-1.5h16.19l-6.22-6.22a.75.75 0 0 1 0-1.06Z" clipRule="evenodd" />
            </svg>
          </button>
          </div>
        </>
      ) : (
        <>
         <div className="inputGroup">
          <input
            data-cy="input-verify-code"
            type="text"
            placeholder="Verification code"
            className="inputField"
            value={code}
            onChange={(e) => setCode(e.target.value)}
          />
          <button 
            data-cy="submit-btn"
            onClick={verifyCode} 
            disabled={loading || code.length !== 6}
            className={`inputSubmit${code.length === 6 ? " valid" : ""}`}
            >
            <svg width={20} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
              <path fillRule="evenodd" d="M12.97 3.97a.75.75 0 0 1 1.06 0l7.5 7.5a.75.75 0 0 1 0 1.06l-7.5 7.5a.75.75 0 1 1-1.06-1.06l6.22-6.22H3a.75.75 0 0 1 0-1.5h16.19l-6.22-6.22a.75.75 0 0 1 0-1.06Z" clipRule="evenodd" />
            </svg>
          </button>
          </div>
        </>
      )}
      {loading && <p>Processing registration...</p>}
      {error && <p style={{ color: "red" }}>{error}</p>}
    </div>
    </div>
  );
}

export default Reg_PhoneVerification;
